import { useOrg } from "@/contexts/OrgContext";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { decimalPlaces, useCountUp, useInView } from "@/hooks/useTenantMotion";
import { LoadingLabel, Skel, SkelLines } from "@/components/home/TenantSkeletons";
import { renderDisplayText } from "@/lib/utils";
import { Star, Shield, Clock, Heart, Award, Leaf, ThumbsUp, Users, type LucideIcon } from "lucide-react";
import { useWhyChooseContent, DEFAULT_WHY_CHOOSE_CONTENT } from "@/hooks/useWhyChooseContent";
import { useCompanyInfoBrief } from "@/hooks/useCompanyInfo";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";
import type { StatItem, FeatureItem } from "@/hooks/useWhyChooseContent";

const STAT_COLS: Record<number, string> = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' };

const ICON_MAP: Record<string, LucideIcon> = { Shield, Clock, Heart, Award, Leaf, ThumbsUp, Users };
const getIconComponent = (name: string): LucideIcon => ICON_MAP[name] ?? Shield;

function resolveWhyChooseTitle(contentTitle: string | null | undefined, orgName: string, usingDefault: boolean) {
  const title = contentTitle?.trim() ?? '';
  if (usingDefault || !title || title === 'Why choose The Polished Version?') {
    return `Why choose ${orgName}?`;
  }
  return title;
}

function parseStatValue(value: string): { numeric: number; prefix: string; suffix: string; isStar: boolean } {
  const isStar = value.includes('★') || value.toLowerCase().includes('star');
  if (isStar) {
    const num = parseFloat(value.replace(/[^\d.]/g, '')) || 5;
    return { numeric: num, prefix: '', suffix: '★', isStar: true };
  }
  const prefix = value.match(/^[^0-9]*/)?.[0] ?? '';
  const suffix = value.match(/[^0-9]*$/)?.[0] ?? '';
  const numeric = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
  return { numeric, prefix, suffix, isStar: false };
}

function StarRating({ target, started }: { target: number; started: boolean }) {
  const filled = Math.round(target);
  return (
    <div className="tp-stars mb-3" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          strokeWidth={0}
          className="tp-star h-5 w-5 fill-current"
          data-on={started && i < filled ? 'true' : 'false'}
          style={{ ['--star' as string]: i }}
        />
      ))}
    </div>
  );
}

function AnimatedStat({ stat, started, index }: { stat: StatItem; started: boolean; index: number }) {
  const { numeric, prefix, suffix, isStar } = parseStatValue(stat.value);
  const decimals = decimalPlaces(stat.value.replace(/[^\d.]/g, ''));
  const count = useCountUp(numeric, started && !isStar, { duration: 1700, decimals });
  const starValue = useCountUp(numeric, started && isStar, { duration: 1100, decimals: decimals || (Number.isInteger(numeric) ? 0 : 1) });

  return (
    <div
      className={`px-6 py-8 md:px-8 md:py-10 ${index % 2 === 1 ? 'border-l tp-hairline' : ''} ${index >= 2 ? 'border-t tp-hairline md:border-t-0' : ''} ${index > 0 ? 'md:border-l' : ''}`}
    >
      {isStar ? (
        <>
          <StarRating target={numeric} started={started} />
          <p className="tp-numeral text-[2.5rem] text-foreground md:text-[3.25rem]" aria-label={stat.value}>
            {starValue}{suffix}
          </p>
        </>
      ) : (
        <p className="tp-numeral text-[2.5rem] text-foreground md:text-[3.25rem]" aria-label={stat.value}>
          {prefix}{count}<span className="text-primary">{suffix}</span>
        </p>
      )}
      <p className="mt-3 max-w-[14rem] text-sm leading-snug text-muted-foreground">{stat.label}</p>
    </div>
  );
}

function WhyChooseSkeleton() {
  return (
    <section className="tp-section" aria-busy="true">
      <LoadingLabel>Loading</LoadingLabel>
      <div className="tp-skeleton container mx-auto px-5 md:px-6" aria-hidden>
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Skel className="h-3 w-20 tp-skel-round" />
            <div className="tp-h2 mt-5"><SkelLines lines={1} widths={['72%']} /></div>
          </div>
          <div className="tp-lead lg:col-span-5 lg:pb-1"><SkelLines lines={2} widths={['100%', '70%']} /></div>
        </div>
        <div className="tp-card mt-12 grid grid-cols-2 overflow-hidden md:mt-16 md:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={`px-6 py-8 md:px-8 md:py-10 ${index % 2 === 1 ? 'border-l tp-hairline' : ''} ${index >= 2 ? 'border-t tp-hairline md:border-t-0' : ''} ${index > 0 ? 'md:border-l' : ''}`}>
              <Skel className="h-10 w-24 rounded-lg md:h-12" />
              <Skel className="mt-4 h-3.5 w-32 tp-skel-round" />
            </div>
          ))}
        </div>
        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 md:mt-20 lg:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex gap-5 border-t tp-hairline pt-7">
              <Skel className="h-11 w-11 shrink-0 rounded-[calc(var(--tp-radius-card)*0.55)]" />
              <div className="flex-1 space-y-2.5">
                <Skel className="h-4 w-2/5 tp-skel-round" />
                <Skel className="h-3.5 w-full tp-skel-round" />
                <Skel className="h-3.5 w-3/4 tp-skel-round" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhyChooseUs() {
  const { org } = useOrg();
  const orgId = useTenantOrgId();

  const contentPendingQuery = useWhyChooseContent(orgId);
  const { data: queryResult } = contentPendingQuery;
  const contentPending = isAwaitingFirstResult(contentPendingQuery);
  const companyPendingQuery = useCompanyInfoBrief(orgId);
  const { data: companyInfo } = companyPendingQuery;
  const companyPending = isAwaitingFirstResult(companyPendingQuery);
  const businessName = companyInfo?.company_name || org.name;
  const usingDefault = queryResult?.usingDefault ?? true;
  // On /demo the default title/intro become a showcase prompt; saved content still wins.
  const demo = useDemoShowcase();
  const baseContent = queryResult?.content ?? DEFAULT_WHY_CHOOSE_CONTENT;
  const content = demo && usingDefault ? { ...baseContent, ...demo.whyChoose } : baseContent;

  // Stats count up once the strip scrolls into view.
  const [statsRef, statsStarted] = useInView<HTMLDivElement>({ threshold: 0.35 });

  if (contentPending || companyPending) return <WhyChooseSkeleton />;

  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="tp-eyebrow" data-reveal>Why us</p>
            <h2 className="tp-h2 mt-5" data-reveal style={{ ['--reveal-delay' as string]: '70ms' }}>
              {renderDisplayText(demo && usingDefault ? content.title : resolveWhyChooseTitle(content.title, businessName, usingDefault))}
            </h2>
          </div>
          <p className="tp-lead lg:col-span-5 lg:pb-1" data-reveal style={{ ['--reveal-delay' as string]: '140ms' }}>
            {renderDisplayText(content.description)}
          </p>
        </div>

        {/* Animated Stats */}
        <div
          ref={statsRef}
          className={`tp-card mt-12 grid grid-cols-2 overflow-hidden md:mt-16 ${STAT_COLS[Math.min(content.stats.length, 4)] ?? 'md:grid-cols-4'}`}
          data-reveal
          style={{ ['--reveal-delay' as string]: '120ms' }}
        >
          {content.stats.map((stat, index) => (
            <AnimatedStat key={stat.label} stat={stat} started={statsStarted} index={index} />
          ))}
        </div>

        {/* Features */}
        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 md:mt-20 lg:grid-cols-3">
          {content.features.map((feature: FeatureItem, i: number) => {
            const Icon = getIconComponent(feature.icon);
            return (
              <div
                key={feature.title}
                className="flex gap-5 border-t tp-hairline pt-7"
                data-reveal
                style={{ ['--reveal-delay' as string]: `${(i % 3) * 90}ms` }}
              >
                <span className="tp-icon-chip">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="tp-h4">
                    {renderDisplayText(feature.title)}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
